import { useState, useCallback, useRef } from 'react';

import AutocompleteInput from 'components/AutocompleteInput';
import SelectProjectPopout from 'components/SelectProjectPopout';
import { ResourceListItem } from 'components/ResourceList';

import IconPlus from 'icons/icon-plus';
import IconProject from 'icons/icon-project';

import { Task, Project } from 'types';

interface ResourceListItemCreateProps {
    resourceType: 'task' | 'milestone',
    projectId?: string,
    autofocus?: boolean,
    autofocusDelay?: number,
    placeholder?: string,
    excludedResources?: string[],
    onCreate?: (names: string[]) => void,
    onSelect?: (resource: any) => void,
    onProjectChange: (project: Project) => void
};

const ResourceListItemCreate = ({ resourceType, projectId, autofocus, autofocusDelay, placeholder, excludedResources, onCreate, onSelect, onProjectChange }: ResourceListItemCreateProps) => {
    const [focused, setFocused] = useState<boolean>(false);
    const [value, setValue] = useState<string>('');
    const element = useRef(null);
    const inputCtrl = useRef<any>(null);

    const handleMarkerClick = () => {
        inputCtrl.current?.focus();
    }

    const handleFocus = useCallback(() => {
        setFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setFocused(false);
    }, []);

    const handleChange = useCallback((nextValue: string) => {
        setValue(nextValue);
    }, []);

    const handleCreate = useCallback(async (name: string) => {
        inputCtrl.current?.reset();

        await onCreate?.([name]);

        setTimeout(() => {
            inputCtrl.current?.focus();
        });
    }, [onCreate]);

    const handlePaste = useCallback(async (event: ClipboardEvent, value: string) => {
        const entries = value
            .split('\n')
            .map(name => (name || '').trim())
            .filter(name => Boolean(name) && name !== '\n\u200B' && name !== decodeURIComponent('%E2%80%8B'));

        if (entries.length > 1) {
            inputCtrl.current?.reset();

            await onCreate?.(entries);

            setTimeout(() => {
                inputCtrl.current?.focus();
            });

            return true;
        }

        return false;
    }, [onCreate]);

    const handleKeyDown = useCallback(async (event: React.KeyboardEvent) => {
        if (!onSelect && event.which === 13) {
            inputCtrl.current?.reset();

            await onCreate?.([value]);

            setTimeout(() => {
                inputCtrl.current?.focus();
            });
        }
    }, [onCreate, value, onSelect]);

    return (
        <ResourceListItem
            id="new"
            isNew={true}
            canEdit={true}
            editing={true}
            focused={focused}
            ref={element}
            markerElement={<IconPlus />}
            onMarkerClick={handleMarkerClick}
            footElement={
                onProjectChange ? (
                    <>
                        <IconProject className="item-foot-icon" />

                        <SelectProjectPopout
                            autoSelectFirst={true}
                            xAlign="left"
                            defaultXPosition="right"
                            selected={projectId}
                            onSelect={onProjectChange}
                        />
                    </>
                ) : null
            }
        >
            <AutocompleteInput
                ref={inputCtrl}
                resourceType={resourceType}
                excludedResources={excludedResources}
                projectId={projectId}
                autofocus={autofocus}
                autofocusDelay={autofocusDelay}
                placeholder={placeholder}
                onSelect={onSelect}
                onPaste={handlePaste}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onChange={handleChange}
                onCreate={handleCreate}
                onKeyDown={handleKeyDown}
                refElement={element.current}
            />
        </ResourceListItem>
    );
}

export default ResourceListItemCreate
